import IpWhitelistDialog from "@/components/dialogs/IpWhitelistDialog";
import Datatable from "@/components/tables/datatable";
import { Switch } from "@/components/ui/switch";
import {
  useAddWhitelistIP,
  useDeleteWhitelistIP,
  useGetWhitelistIPs,
  useGetWhitelistStatus,
  useUpdateWhitelistIpStatus,
} from "@/services/admin.service";
import { Button, Chip, Input, Radio, getKeyValue } from "@nextui-org/react";
import { Key, useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";

const columns: any[] = [
  {
    key: "email",
    label: "Email",
  },
  {
    key: "whitelistIps",
    label: "Ip Whitelist",
  },
  {
    key: "addip",
    label: "Add IP",
  },
];

const IpWhitelistTab = () => {
  const [refetch, setRefetch] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const { data: whitelistStatus } = useGetWhitelistStatus();
  const { mutate: deleteIp } = useDeleteWhitelistIP();
  const { mutate: addIp } = useAddWhitelistIP();
  const { mutate: updateStatus } = useUpdateWhitelistIpStatus();
  const query = useGetWhitelistIPs;

  const [isActiveEnabled, setIsActiveEnabled] = useState(
    whitelistStatus?.data?.status,
  );

  useEffect(() => {
    setIsActiveEnabled(whitelistStatus?.data?.data?.ipWhitelistStatus);
  }, [whitelistStatus]);

  const changeWhitelistStatus = () => {
    const newStatus = !isActiveEnabled;
    updateStatus(newStatus, {
      onSuccess: () => {
        toast.success("Status updated");
      },
      onError: () => {
        toast.error("Something went wrong.");
      },
    });

    setIsActiveEnabled(!isActiveEnabled);
  };

  const renderCell = useCallback(
    (data: any[], item: any, columnKey: Key) => {
      const onDeleteIp = (ip: string, email: string) => {
        deleteIp(
          {
            email,
            ip,
          },
          {
            onSuccess: () => {
              toast.success("IP deleted");
              setRefetch(true);
            },
            onError: () => {
              toast.error("Something went wrong.");
            },
          },
        );
      };

      const onAddIp = (e: any) => {
        e.preventDefault();

        const form = Object.fromEntries(new FormData(e.target).entries());

        const regex =
          /^(?:(?:25[0-5]|[0-2]?[0-4]?[0-9]|[0-1]?[0-9]?[0-9])\.){3}(?:(?:25[0-5]|[0-2]?[0-4]?[0-9]|[0-1]?[0-9]?[0-9]))$/;

        if (!regex.test(form.ip as string)) {
          toast.error("Invalid IP address format.");
          return;
        }

        addIp(
          {
            email: form.email as string,
            ip: form.ip as string,
          },
          {
            onSuccess: () => {
              toast.success("IP added");
              setRefetch(true);
            },
            onError: () => {
              toast.error("Cannot add that IP address. Please check again");
            },
          },
        );
      };

      switch (columnKey) {
        case "whitelistIps":
          return (
            <div className="grid grid-cols-3 gap-x-[166px] sm:mr-[-23rem] sm:gap-1">
              {item[columnKey]?.map((ip: any, index: any) => (
                <div key={index} className="mb-1 flex items-center">
                  <Chip
                    className="whitelist-chip"
                    onClose={() => onDeleteIp(ip, item["email"])}
                  >
                    <span className="font-light">{ip}</span>
                  </Chip>
                </div>
              ))}
            </div>
          );

        case "addip":
          return (
            <form
              className="float-right flex pl-[10rem] sm:pl-[22rem]"
              onSubmit={onAddIp}
            >
              <Input
                className="hidden"
                name="email"
                defaultValue={item["email"]}
              />

              <Input
                name="ip"
                className="input-bg w-40 sm:w-40"
                placeholder="Input New Ip Address"
                classNames={{
                  inputWrapper: [
                    "!cursor-text",
                    "rounded-md",
                    "border",
                    "border-solid",
                    "border-white",
                    "border-opacity-50",
                    "input-bg",
                    "text-[13px]",
                  ],
                }}
              ></Input>
              <Button
                className="admin-common-button-gradient font-ubuntu ml-5 rounded-md text-base font-bold"
                type="submit"
              >
                Add IP
              </Button>
            </form>
          );

        default:
          return (
            <div className="font-normal">
              {getKeyValue(item, columnKey as string) || "-"}
            </div>
          );
      }
    },
    [addIp, deleteIp],
  );

  return (
    <div>
      <div className="items-center sm:ml-0 sm:flex">
        <div className="mb-1 mt-2 flex items-center justify-start sm:mt-0">
          <div>
            <h2 className="text-sm font-bold text-white sm:text-base">
              INACTIVE / ACTIVE WHITELIST
            </h2>
          </div>
          <div>
            <label
              htmlFor="activestate"
              className="relative ml-[107px] mt-1 inline-flex cursor-pointer items-center sm:ml-5"
            >
              <IpWhitelistDialog
                isCurrentlyEnabled={isActiveEnabled}
                onSuccess={changeWhitelistStatus}
                trigger={
                  <Switch
                    id="activestate"
                    className={`border-white ${isActiveEnabled && "bg-success"}`}
                    defaultChecked={isActiveEnabled}
                    checked={isActiveEnabled}
                    onClick={() => setIsDialogOpen(true)}
                  />
                }
              />
            </label>
          </div>
        </div>
      </div>
      <div className="mt-5">
        <Datatable
          columns={columns}
          renderCell={renderCell}
          query={query}
          customFilters={[]}
          searchFields={[
            {
              label: "Email",
              value: "email",
            },
          ]}
          triggerRefetch={refetch}
          onRefetch={() => setRefetch(false)}
        />
      </div>
    </div>
  );
};

export default IpWhitelistTab;
